import { LoggableError } from '@yandex-int/messenger.errors';
import { Event } from '@yandex-int/messenger.utils';
import debounce from 'lodash/debounce';

import { AudioLevelAnalyser } from '../analysers/audio-level-analyser';
import { SafeLogErrorOptions } from '../log-error';
import { createLogger } from '../loggerFacade';
import { getAudioPlayerError } from './get-audio-player-error';
import { getAudioTrackFromMediaStream } from './get-audio-track';
import { getSupportedMediaSupportedAudioCodec, isMediaRecorderSupported } from './media-recorder-support';

export enum States {
    IDLE = 'idle',
    RECORDING = 'recording',
    PLAYING = 'playing',
    DESTROYED = 'destroyed',
}
export enum AudioPlayerStatus {
    IDLE = 'idle',
    PLAYING = 'playing',
    PAUSED = 'paused',
}
const DEFAULT_DEVICE = 'default';

const logger = createLogger('AudioHandler');
const recorderCodec = getSupportedMediaSupportedAudioCodec();
export class AudioHandler {
    private mediaRecorder: MediaRecorder | null = null;
    private recordedChunks: Blob[] = [];
    private recordedBlob: Blob | null = null;
    private audioContext: AudioContext;
    private audioPlayer: HTMLAudioElement;

    private analyzerPlayer: AudioBufferSourceNode | null = null;
    private mediaRecorderSourceNode: MediaStreamAudioSourceNode | null = null;
    private mediaRecorderDestinationNode: MediaStreamAudioDestinationNode;
    private recordingAudioTrack?: MediaStreamTrack | null;

    private level = 0;
    private analyzer: AudioLevelAnalyser;
    private _state: States;
    private audioSrcUrl: string | null = null;
    public onStateChanged = new Event<States>();
    public onRecordStart = new Event<States>();
    public onPlayStart = new Event<States>();
    public onRecordStop = new Event<States>();
    public onPlayStop = new Event<States>();
    private stopAnalyzer: () => void;

    constructor(audioContext: AudioContext, audioLevelAnalyser?: AudioLevelAnalyser) {
        this.audioPlayer = new Audio();
        this.audioPlayer.addEventListener('error', () => {
            if (this.state === States.PLAYING) {
                const errorCode = this.audioPlayer.error?.code ?? 0;
                logger.error(new Error(getAudioPlayerError(errorCode)) as LoggableError, {
                    method: 'audioPlayerErrorHandler',
                    additional: {
                        code: errorCode,
                    },
                });
                this.stopPlayback();
            }
        });
        this.audioContext = audioContext;

        this.mediaRecorderDestinationNode = this.audioContext.createMediaStreamDestination();
        this.analyzer = audioLevelAnalyser ?? new AudioLevelAnalyser(audioContext);

        this.stopAnalyzer = this.initAnalyzerCallback();
        this._state = States.IDLE;
    }

    private setState(state: States) {
        this._state = state;
        this.onStateChanged.dispatch(state);
    }

    async playRecorded(deviceId?: string, loop = false) {
        if (!this.recordedBlob) {
            return;
        }

        const arrayBuffer = await this.recordedBlob.arrayBuffer();
        this.play(arrayBuffer, deviceId, loop);
        this.recordedBlob = null;
        this.recordedChunks = [];
    }

    async playFromBase64(content: string, deviceId?: string, loop = false, playCount = 1, initialSetSink = false) {
        const byteCharacters = atob(content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const arrayBuffer = new Uint8Array(byteNumbers).buffer;
        this.play(arrayBuffer, deviceId, loop, playCount, initialSetSink);
    }

    public async changeOutputDevice(deviceId: string) {
        if (this.audioPlayer.setSinkId) {
            // https://w3c.github.io/mediacapture-output/#attributes
            await this.audioPlayer.setSinkId(deviceId === DEFAULT_DEVICE ? '' : deviceId);
        }
    }
    private createAnalyzerPlayer(content: AudioBuffer) {
        const analyzerPlayer = this.audioContext.createBufferSource();
        analyzerPlayer.buffer = content;
        this.analyzer?.connect(analyzerPlayer);
        this.analyzerPlayer = analyzerPlayer;
        return analyzerPlayer;
    }
    private loadAudioSrc = (audioSrcUrl: string) => {
        this.audioPlayer.src = audioSrcUrl;
        this.audioPlayer.load();
        return new Promise((res, rej) => {
            this.audioPlayer.addEventListener('canplaythrough', res, { once: true });
            this.audioPlayer.addEventListener('error', rej, { once: true });
        });
    };
    private destroyAnalyzerPlayer() {
        this.analyzerPlayer?.stop();
        if (this.analyzerPlayer) {
            this.analyzer?.disconnect(this.analyzerPlayer);
        }
        this.analyzerPlayer = null;
    }
    private async play(content: ArrayBuffer, deviceId?: string, loop = false, playCount = 1, requiredSetSink = false) {
        if (this._state === States.PLAYING || this.state === States.DESTROYED) {
            return;
        }
        const errorPlayHandler = (error: unknown, options: Omit<SafeLogErrorOptions, 'method'> = {}) => {
            logger.error(error, {
                method: 'play',
                ...options,
            });
            this.stopPlayback();
        };
        if (typeof deviceId === 'string' && this.audioPlayer.setSinkId) {
            try {
                await this.changeOutputDevice(deviceId);
            } catch (error) {
                if (requiredSetSink) {
                    errorPlayHandler(error, {
                        additional: {},
                    });
                    return;
                }
                logger.warn(error, {
                    method: 'play',
                    additional: {
                        deviceId,
                    },
                });
            }
        }
        const blob = new Blob([content]);

        const audioBuffer = await this.audioContext.decodeAudioData(content);
        this.audioSrcUrl = URL.createObjectURL(blob);
        await this.loadAudioSrc(this.audioSrcUrl);
        if (loop) {
            const analyzerPlayer = this.createAnalyzerPlayer(audioBuffer);
            this.audioPlayer.currentTime = 0;
            this.audioPlayer.loop = true;
            analyzerPlayer.loop = true;
            analyzerPlayer.start();
            this.audioPlayer.play().catch(errorPlayHandler);
        } else {
            if (playCount <= 0) {
                return;
            }

            let count = 0;
            this.audioPlayer.loop = false;
            const playNext = () => {
                const analyzerPlayer = this.createAnalyzerPlayer(audioBuffer);
                this.audioPlayer.onended = () => {
                    count++;
                    if (count >= playCount) {
                        this.stopPlayback();
                    } else if (this._state === States.PLAYING) {
                        this.destroyAnalyzerPlayer();
                        playNext();
                    }
                };
                this.audioPlayer.pause();
                this.audioPlayer.currentTime = 0;
                analyzerPlayer.start();

                this.audioPlayer.play().catch(errorPlayHandler);
            };
            playNext();
        }
        if (this._state !== States.DESTROYED) {
            this.setState(States.PLAYING);
        }

        this.onPlayStart.dispatch(this.state);
    }

    async startRecording(stream: MediaStream) {
        if (
            this.mediaRecorder?.state === 'recording' ||
            !isMediaRecorderSupported() ||
            this.state === States.DESTROYED
        ) {
            return;
        }

        const clonedTrack = getAudioTrackFromMediaStream(stream)?.clone();
        if (!clonedTrack) {
            return;
        }

        clonedTrack.enabled = true;
        this.recordedChunks = [];
        this.mediaRecorderDestinationNode = this.audioContext.createMediaStreamDestination();
        this.setAudioTrackForRecording(clonedTrack);
        this.mediaRecorder = new MediaRecorder(this.mediaRecorderDestinationNode.stream, {
            mimeType: recorderCodec,
        });

        this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
            if (event.data.size > 0) {
                this.recordedChunks.push(event.data);
            }
        };

        this.mediaRecorder.start();
        this.setState(States.RECORDING);

        this.onRecordStart.dispatch(this.state);
    }
    setAudioTrackForRecording(track: MediaStreamTrack) {
        this.recordingAudioTrack = track;
        this.mediaRecorderSourceNode = this.audioContext.createMediaStreamSource(new MediaStream([track]));
        this.mediaRecorderSourceNode.connect(this.mediaRecorderDestinationNode);
    }

    changeInputDevice(stream: MediaStream) {
        if (this.mediaRecorder?.state !== 'recording') {
            return;
        }
        if (this.mediaRecorderSourceNode) {
            this.mediaRecorderSourceNode.disconnect();
            this.mediaRecorderSourceNode = null;
        }
        if (this.recordingAudioTrack) {
            this.recordingAudioTrack.stop();
        }
        const clonedTrack = getAudioTrackFromMediaStream(stream)?.clone();
        if (!clonedTrack) {
            return;
        }
        clonedTrack.enabled = true;
        this.setAudioTrackForRecording(clonedTrack);
    }

    emergencyStop() {
        this._stopRecording();
        this._stopPlayback();
        if (this._state !== States.DESTROYED) {
            this.setState(States.IDLE);
        }
    }

    private _stopRecording() {
        if (!this.mediaRecorder) {
            return;
        }
        this.mediaRecorder.stop();
        if (this.mediaRecorderSourceNode) {
            this.mediaRecorderSourceNode.disconnect();
            this.mediaRecorderSourceNode = null;
        }
        if (this.recordingAudioTrack) {
            this.recordingAudioTrack.stop();
        }
        this.mediaRecorder = null;
    }

    private _stopPlayback() {
        if (this._state !== States.PLAYING) {
            return;
        }
        this.destroyAnalyzerPlayer();
        this.audioPlayer.onended = null;
        this.audioPlayer.pause();

        if (this.audioSrcUrl) {
            URL.revokeObjectURL(this.audioSrcUrl);
            this.audioSrcUrl = null;
        }
    }

    stopRecording() {
        if (!this.mediaRecorder) {
            return;
        }

        this.mediaRecorder.onstop = () => {
            this.recordedBlob = new Blob(this.recordedChunks, { type: recorderCodec });
            this.onRecordStop.dispatch(this.state);
        };

        this._stopRecording();
    }

    stopPlayback() {
        this._stopPlayback();
        if (this._state !== States.DESTROYED) {
            this.setState(States.IDLE);
        }

        this.onPlayStop.dispatch(this.state);
    }

    get volumeLevel(): number {
        return this.level;
    }

    private initAnalyzerCallback() {
        const setAdjustedValue = (audioLvl: number) => {
            this.level = Math.min(audioLvl, 1);
        };

        const resetLevelAfterTime = debounce(() => setAdjustedValue(0), 300);

        const analyzerCallback = (audioLvl: number) => {
            resetLevelAfterTime();
            setAdjustedValue(audioLvl);
        };

        this.analyzer.start({
            callback: analyzerCallback,
        });
        const stopAnalyser = () => {
            this.analyzer.stop(analyzerCallback);
        };
        return stopAnalyser;
    }

    get state() {
        return this._state;
    }

    getAudioPlayerStatus() {
        if (this.audioPlayer.paused) {
            return AudioPlayerStatus.PAUSED;
        }
        if (this.audioPlayer.ended) {
            return AudioPlayerStatus.IDLE;
        }
        return AudioPlayerStatus.PLAYING;
    }
    public destroy() {
        this.emergencyStop();
        this.stopAnalyzer();
        this.setState(States.DESTROYED);
    }
}
